import React, { useCallback, useEffect, useRef, useState } from "react";
import { Check, ImagePlus, Loader2, Pencil, Trash2 } from "lucide-react";

import PrivateReferenceImage from "../masterOrder/PrivateReferenceImage";
import ExplicitVisualPreferenceInlineV1 from "./ExplicitVisualPreferenceInlineV1";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../ui/dialog";
import { MAX_BRAND_REFERENCES, referenceCountLabel } from "../../lib/brandReferences.mjs";
import {
  createBrandReference,
  deleteBrandReference,
  listBrandReferences,
  updateBrandReference,
  uploadUserMediaFile,
} from "../../lib/brandStudioApi";

const ALLOWED_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);
const MAX_FILE_BYTES = 15 * 1024 * 1024;

const REFERENCE_SIGNALS = [
  ["color", "Цвет"],
  ["typography", "Типографика"],
  ["composition", "Композиция"],
  ["photography", "Фотография"],
  ["graphics", "Графика"],
  ["packaging", "Упаковка"],
  ["material_texture", "Материал / фактура"],
  ["mood", "Настроение"],
  ["logo_mark", "Логотип / знак"],
  ["brandbook_presentation", "Подача брендбука"],
  ["whole", "Всё целиком"],
  ["other", "Другое"],
];

const SIGNAL_LABELS = Object.fromEntries(REFERENCE_SIGNALS);

function errorText(error) {
  return error?.response?.data?.detail || "Не удалось выполнить действие. Попробуй ещё раз.";
}

function ReferenceCard({ reference, onOpen }) {
  const ratio =
    reference.width && reference.height
      ? `${reference.width} / ${reference.height}`
      : "4 / 3";

  return (
    <article
      className="mb-3 break-inside-avoid overflow-hidden rounded-[18px] border border-[#E4D7C5] bg-[#F7F1E8] md:mb-4"
      style={{ boxShadow: "0 10px 30px rgba(74,55,40,0.06)" }}
      data-testid="brand-reference-v2-item"
    >
      <button
        type="button"
        onClick={() => onOpen(reference.id)}
        className="group relative block w-full overflow-hidden text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#9A7B5A]"
        aria-label="Открыть референс"
        data-testid="brand-reference-v2-open"
      >
        <div className="w-full overflow-hidden bg-[#F3EBDD]" style={{ aspectRatio: ratio }}>
          <PrivateReferenceImage
            reference={reference}
            className="block h-full w-full object-cover transition duration-300 group-hover:scale-[1.015]"
          />
        </div>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-end bg-gradient-to-t from-black/25 to-transparent px-3 pb-3 pt-8 opacity-0 transition group-hover:opacity-100">
          <span className="rounded-full bg-[#FFFDF9]/90 px-3 py-1 text-[11px] text-[#4A3728]">
            Открыть
          </span>
        </div>
      </button>
      {!!reference.signals?.length && (
        <button
          type="button"
          onClick={() => onOpen(reference.id)}
          className="block w-full border-t border-[#E7DAC7] bg-[#FFFDF9] px-3.5 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#9A7B5A]"
          data-testid="brand-reference-v2-signal-preview"
        >
          <span className="block text-[9px] uppercase tracking-[0.16em] text-[#9A8066]">
            Здесь важно
          </span>
          <span className="mt-2 flex flex-wrap gap-1.5">
            {reference.signals.map((signal) => (
              <span
                key={signal}
                className="rounded-full border border-[#DED0BC] bg-[#F7F1E8] px-2.5 py-1 text-[10px] text-[#654E3D]"
              >
                {SIGNAL_LABELS[signal] || signal}
              </span>
            ))}
          </span>
        </button>
      )}
      {reference.note && (
        <button
          type="button"
          onClick={() => onOpen(reference.id)}
          className="block w-full border-t border-[#E7DAC7] bg-[#FFFDF9] px-3.5 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#9A7B5A]"
          data-testid="brand-reference-v2-note-preview"
        >
          <span className="block text-[9px] uppercase tracking-[0.16em] text-[#9A8066]">
            Моя заметка
          </span>
          <span className="mt-1.5 block line-clamp-3 text-[11.5px] leading-5 text-[#5D4736]">
            {reference.note}
          </span>
        </button>
      )}
    </article>
  );
}

export default function BrandReferencesWorkspaceV2({ onContinue = null }) {
  const pickerRef = useRef(null);
  const [status, setStatus] = useState("loading");
  const [references, setReferences] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [noteEditing, setNoteEditing] = useState(false);
  const [signalDraft, setSignalDraft] = useState([]);
  const [visualStyleLoading, setVisualStyleLoading] = useState(false);
  const [visualStyleLine, setVisualStyleLine] = useState(0);

  const selectedReference =
    references.find((item) => item.id === selectedId) || null;
  const persistedSignals = selectedReference?.signals || [];
  const signalsMatchSaved =
    signalDraft.length === persistedSignals.length &&
    signalDraft.every((value) => persistedSignals.includes(value));

  const load = useCallback(async () => {
    setStatus("loading");
    setError("");
    try {
      setReferences(await listBrandReferences());
      setStatus("ready");
    } catch (requestError) {
      setError(errorText(requestError));
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openReference = (id) => {
    const reference = references.find((item) => item.id === id);
    setConfirmDelete(false);
    setNoteDraft(reference?.note || "");
    setNoteEditing(!reference?.note);
    setSignalDraft(reference?.signals || []);
    setSelectedId(id);
  };

  const upload = async (event) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = "";
    if (!selected.length || busy) return;

    const available = MAX_BRAND_REFERENCES - references.length;
    if (available <= 0) {
      setError("В одной подборке можно сохранить до 20 референсов.");
      return;
    }

    setBusy(true);
    setError("");

    let next = [...references];
    let failures = 0;
    const accepted = selected.slice(0, available);

    for (const file of accepted) {
      if (!ALLOWED_TYPES.has(file.type) || file.size > MAX_FILE_BYTES) {
        failures += 1;
        continue;
      }

      try {
        const media = await uploadUserMediaFile(file, { title: "Референс бренда" });
        const created = await createBrandReference({
          media_id: media.id,
          note: "",
          signals: [],
          order: next.length,
        });
        next = [...next, created];
        setReferences(next);
      } catch {
        failures += 1;
      }
    }

    if (selected.length > available) {
      setError("В одной подборке можно сохранить до 20 референсов.");
    } else if (failures) {
      setError(
        `Не удалось добавить: ${failures}. Остальные изображения сохранены.`,
      );
    }

    setBusy(false);
  };

  const toggleSignal = (value) => {
    setSignalDraft((current) => {
      if (value === "whole") {
        return current.includes("whole") ? [] : ["whole"];
      }
      const withoutWhole = current.filter((item) => item !== "whole");
      return withoutWhole.includes(value)
        ? withoutWhole.filter((item) => item !== value)
        : [...withoutWhole, value];
    });
  };

  const saveSignals = async () => {
    if (!selectedReference || busy) return;
    const currentSignals = selectedReference.signals || [];
    if (
      signalDraft.length === currentSignals.length &&
      signalDraft.every((value) => currentSignals.includes(value))
    ) {
      return;
    }

    setBusy(true);
    setError("");

    try {
      const updated = await updateBrandReference(selectedReference.id, {
        signals: signalDraft,
      });
      setReferences((current) =>
        current.map((item) => (item.id === updated.id ? updated : item)),
      );
      setSignalDraft(updated.signals || []);
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setBusy(false);
    }
  };

  const saveNote = async () => {
    if (!selectedReference || busy) return;
    const note = noteDraft.trim();
    if (note === (selectedReference.note || "")) return;

    setBusy(true);
    setError("");

    try {
      const updated = await updateBrandReference(selectedReference.id, { note });
      setReferences((current) =>
        current.map((item) => (item.id === updated.id ? updated : item)),
      );
      setNoteDraft(updated.note || "");
      setNoteEditing(false);
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setBusy(false);
    }
  };

  const startVisualStyle = async () => {
    if (visualStyleLoading) return;
    setVisualStyleLoading(true);
    setVisualStyleLine(0);

    for (let index = 1; index < 4; index += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, 750));
      setVisualStyleLine(index);
    }
    await new Promise((resolve) => window.setTimeout(resolve, 750));

    setVisualStyleLoading(false);
    onContinue?.();
  };

  const remove = async () => {
    if (!selectedReference || busy) return;

    setBusy(true);
    setError("");

    try {
      await deleteBrandReference(selectedReference.id);

      const remaining = references.filter(
        (item) => item.id !== selectedReference.id,
      );

      await Promise.all(
        remaining.map((item, order) =>
          item.order === order
            ? Promise.resolve()
            : updateBrandReference(item.id, { order }),
        ),
      );

      setReferences(await listBrandReferences());
      setSelectedId(null);
      setConfirmDelete(false);
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setBusy(false);
    }
  };

  if (status === "loading") {
    return (
      <section
        className="rounded-[22px] border border-[#E1D4C2] bg-[#FFFDF9] p-5 md:p-7"
        data-testid="brand-references-v2-loading"
      >
        <div className="inline-flex items-center gap-2 text-sm text-[#796652]">
          <Loader2 size={16} className="animate-spin" /> Открываю твои референсы…
        </div>
      </section>
    );
  }

  if (status === "error") {
    return (
      <section className="rounded-[22px] border border-[#E2CFC8] bg-[#FFF8F4] p-5 md:p-7">
        <h2 className="font-serif text-[28px] text-[#2B211A]">
          Не удалось открыть референсы
        </h2>
        <p className="mt-2 text-[13px] leading-5 text-[#6B443A]">{error}</p>
        <button
          type="button"
          onClick={load}
          className="mt-4 min-h-11 rounded-full border border-[#CDB899] bg-white px-5 text-sm text-[#4A3728]"
        >
          Повторить
        </button>
      </section>
    );
  }

  return (
    <section
      className="rounded-[22px] border border-[#E1D4C2] bg-[#FFFDF9] p-5 md:p-7"
      data-testid="brand-references-workspace-v2"
    >
      <input
        ref={pickerRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        className="sr-only"
        onChange={upload}
        aria-label="Выбрать изображения для референсов"
        data-testid="brand-references-v2-input"
      />

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="max-w-2xl">
          <div className="text-[9.5px] uppercase tracking-[0.22em] text-[#8A6D53]">
            ТВОИ ВИЗУАЛЬНЫЕ ОРИЕНТИРЫ
          </div>
          <h2 className="mt-3 font-serif text-[31px] leading-none text-[#2B211A] md:text-[36px]">
            Покажи, что тебе нравится
          </h2>
          <p className="mt-3 text-[13.5px] leading-6 text-[#655141]">
            Можно добавить фотографии, упаковку, сайты, интерьеры, шрифты, цвета
            или любые другие визуальные примеры. Не важно, относятся они к твоей
            профессии или нет.
          </p>
          <p className="mt-2 text-[12.5px] leading-5 text-[#806B55]">
            Сейчас просто собери всё, что тебя цепляет. Разберём почему — следующим шагом.
          </p>
        </div>

        <div
          className="shrink-0 rounded-full bg-[#F3E9DB] px-3 py-1.5 text-[12px] text-[#765D44]"
          data-testid="brand-reference-v2-count"
        >
          {referenceCountLabel(references.length)}
        </div>
      </div>

      {!references.length ? (
        <div className="mt-7 overflow-hidden rounded-[20px] bg-[#F3EBDD] px-5 py-7 md:px-7 md:py-9">
          <div className="max-w-xl">
            <ImagePlus size={26} strokeWidth={1.5} className="text-[#8B6B4D]" />
            <h3 className="mt-5 font-serif text-[26px] leading-tight text-[#2B211A] md:text-[30px]">
              Начни собирать свой визуальный мир
            </h3>
            <p className="mt-3 text-[13px] leading-6 text-[#655141]">
              Можно выбрать сразу несколько изображений. До 20 референсов в одной подборке.
            </p>
            <button
              type="button"
              disabled={busy}
              onClick={() => pickerRef.current?.click()}
              className="mt-5 inline-flex min-h-12 items-center gap-2 rounded-full bg-[#3D2A20] px-6 text-sm text-[#FFF8ED] disabled:opacity-45"
              data-testid="brand-references-v2-add"
            >
              {busy ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <ImagePlus size={16} />
              )}
              Добавить референсы
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="mt-7 flex flex-wrap items-center justify-between gap-3 border-t border-[#E8DCCB] pt-5">
            <p className="max-w-xl text-[12.5px] leading-5 text-[#806B55]">
              Это твоя живая визуальная доска. Можно возвращаться и добавлять новые ориентиры.
            </p>
            <button
              type="button"
              disabled={busy || references.length >= MAX_BRAND_REFERENCES}
              onClick={() => pickerRef.current?.click()}
              className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[#CDB899] bg-white px-5 text-sm text-[#4A3728] disabled:opacity-45"
              data-testid="brand-references-v2-add-more"
            >
              {busy ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <ImagePlus size={16} />
              )}
              Добавить ещё
            </button>
          </div>

          <div
            className="mt-5 columns-2 gap-3 md:columns-3 md:gap-4 lg:columns-4"
            data-testid="brand-references-v2-board"
          >
            {references.map((reference) => (
              <ReferenceCard
                key={reference.id}
                reference={reference}
                onOpen={openReference}
              />
            ))}
          </div>

          <ExplicitVisualPreferenceInlineV1 />

          <section
            className="mt-10 overflow-hidden rounded-[20px] border border-[#DCCDB8] bg-[#F6EFE5] px-5 py-6 md:px-7 md:py-7"
            data-testid="brand-references-v2-complete"
          >
            {!visualStyleLoading ? (
              <div className="max-w-2xl">
                <div className="text-[9.5px] uppercase tracking-[0.2em] text-[#8A6D53]">
                  ВСЁ ГОТОВО
                </div>
                <h3 className="mt-3 font-serif text-[26px] leading-tight text-[#2B211A] md:text-[30px]">
                  Всё готово для первого визуального взгляда
                </h3>
                <p className="mt-3 text-[13px] leading-6 text-[#655141]">
                  Я соединю твой рассказ, данные бизнеса, задачи, референсы, отметки, заметки,
                  типографику и решение по логотипу и соберу первый целостный взгляд на бренд.
                </p>
                <p className="mt-2 text-[12.5px] leading-5 text-[#806B55]">
                  Не обязательно заполнять все 20 референсов. К этой доске всегда можно вернуться.
                </p>
                <button
                  type="button"
                  onClick={startVisualStyle}
                  className="mt-5 min-h-12 rounded-full bg-[#3D2A20] px-6 text-sm text-[#FFF8ED]"
                  data-testid="brand-references-v2-build-style"
                >
                  Собрать мой визуальный стиль
                </button>
              </div>
            ) : (
              <div className="max-w-2xl py-2" data-testid="brand-references-v2-building-style">
                <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#8A6D53]">
                  <Loader2 size={14} className="animate-spin" /> СОБИРАЮ
                </div>
                <h3 className="mt-3 font-serif text-[27px] leading-tight text-[#2B211A] md:text-[31px]">
                  Собираю твой визуальный мир…
                </h3>
                <p className="mt-3 text-[13px] leading-6 text-[#655141]" data-testid="brand-references-v2-loading-line">
                  {[
                    "Смотрю, что повторяется в твоих референсах.",
                    "Учитываю, какие детали ты отметила.",
                    "Соединяю цвет, типографику, материалы и характер.",
                    "Проверяю, как это может работать на реальных носителях.",
                  ][visualStyleLine]}
                </p>
              </div>
            )}
          </section>
        </>
      )}

      {error && (
        <div className="mt-5 rounded-xl border border-[#E2CFC8] bg-[#FFF8F4] p-4 text-[13px] leading-5 text-[#6B443A]">
          {error}
        </div>
      )}

      <Dialog
        open={Boolean(selectedReference)}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedId(null);
            setNoteDraft("");
            setNoteEditing(false);
            setSignalDraft([]);
            setConfirmDelete(false);
          }
        }}
      >
        <DialogContent className="bs-action-workspace w-[calc(100%-1.5rem)] max-w-5xl max-h-[calc(100vh-1.5rem)] overflow-y-auto rounded-[20px] border-[#E2D5C3] bg-[#FFFDF9] p-0">
          {selectedReference && (
            <div
              className="grid min-w-0 md:grid-cols-[minmax(0,1.4fr)_minmax(250px,.6fr)]"
              data-testid="brand-reference-v2-detail"
            >
              <div className="grid min-h-[300px] max-h-[78vh] place-items-center overflow-hidden bg-[#F3EBDD] p-4">
                <PrivateReferenceImage
                  reference={selectedReference}
                  className="block max-h-[72vh] max-w-full object-contain"
                />
              </div>

              <div className="min-w-0 p-5 pt-12 md:p-7 md:pt-12">
                <DialogTitle className="font-serif text-[27px] font-normal leading-tight text-[#2B211A]">
                  Твой референс
                </DialogTitle>
                <DialogDescription className="mt-3 text-[12.5px] leading-5 text-[#806B55]">
                  Сначала отметь, что именно здесь для тебя важно. Потом, если хочется,
                  уточни детали своими словами.
                </DialogDescription>

                <div className="mt-6" data-testid="brand-reference-v2-signals">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <span className="text-[10px] uppercase tracking-[0.16em] text-[#8A6D53]">
                      Что здесь для тебя важно?
                    </span>
                    {!!persistedSignals.length && signalsMatchSaved && (
                      <span
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#EEF0DF] px-3 py-1.5 text-[11.5px] text-[#586044]"
                        data-testid="brand-reference-v2-signals-saved"
                      >
                        <Check size={13} /> Сохранено
                      </span>
                    )}
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {REFERENCE_SIGNALS.map(([value, label]) => {
                      const active = signalDraft.includes(value);
                      return (
                        <button
                          key={value}
                          type="button"
                          aria-pressed={active}
                          disabled={busy}
                          onClick={() => toggleSignal(value)}
                          className={
                            "min-h-9 rounded-full border px-3.5 py-2 text-[11.5px] transition disabled:opacity-45 " +
                            (active
                              ? "border-[#8B6B4D] bg-[#F3E9DB] text-[#4A3728]"
                              : "border-[#DCCDB8] bg-white text-[#6B5543]")
                          }
                          data-testid={"brand-reference-v2-signal-" + value}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>

                  {!signalsMatchSaved && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={saveSignals}
                      className="mt-4 inline-flex min-h-10 items-center gap-2 rounded-full bg-[#3D2A20] px-4 text-[12px] text-[#FFF8ED] disabled:opacity-40"
                      data-testid="brand-reference-v2-signals-save"
                    >
                      {busy && <Loader2 size={14} className="animate-spin" />}
                      Сохранить отметки
                    </button>
                  )}
                </div>

                <div className="mt-7 border-t border-[#E8DCCB] pt-6">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <span className="text-[10px] uppercase tracking-[0.16em] text-[#8A6D53]">
                      Моя заметка
                    </span>
                    {selectedReference.note && !noteEditing && (
                      <span
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#EEF0DF] px-3 py-1.5 text-[11.5px] text-[#586044]"
                        data-testid="brand-reference-v2-note-saved"
                      >
                        <Check size={13} /> Сохранено
                      </span>
                    )}
                  </div>

                  {noteEditing ? (
                    <>
                      <textarea
                        value={noteDraft}
                        onChange={(event) => setNoteDraft(event.target.value)}
                        rows={6}
                        maxLength={1000}
                        placeholder="Например: нравится глубокий navy и тиснение, но не эта типографика."
                        className="mt-2 w-full resize-y rounded-[14px] border border-[#DCCDB8] bg-white px-3.5 py-3 text-[13px] leading-6 text-[#3E2E20] outline-none transition placeholder:text-[#B09C87] focus:border-[#8C6C49]"
                        data-testid="brand-reference-v2-note"
                      />
                      <div className="mt-3 flex flex-wrap items-center gap-3">
                        <button
                          type="button"
                          disabled={
                            busy ||
                            noteDraft.trim() === (selectedReference.note || "")
                          }
                          onClick={saveNote}
                          className="inline-flex min-h-11 items-center gap-2 rounded-full bg-[#3D2A20] px-5 text-[12.5px] text-[#FFF8ED] disabled:opacity-40"
                          data-testid="brand-reference-v2-note-save"
                        >
                          {busy && <Loader2 size={14} className="animate-spin" />}
                          {selectedReference.note ? "Сохранить изменения" : "Сохранить заметку"}
                        </button>
                        {selectedReference.note && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => {
                              setNoteDraft(selectedReference.note || "");
                              setNoteEditing(false);
                            }}
                            className="min-h-11 rounded-full border border-[#D8C7AE] bg-white px-4 text-[12px] text-[#4A3728] disabled:opacity-45"
                            data-testid="brand-reference-v2-note-cancel"
                          >
                            Отмена
                          </button>
                        )}
                        <span className="text-[11px] text-[#9A8066]">
                          {noteDraft.length}/1000
                        </span>
                      </div>
                    </>
                  ) : (
                    <div
                      className="mt-2 rounded-[14px] border border-[#E4D7C5] bg-[#FFFDF9] p-4"
                      data-testid="brand-reference-v2-note-view"
                    >
                      <p className="whitespace-pre-wrap text-[13px] leading-6 text-[#4A3728]">
                        {selectedReference.note}
                      </p>
                      <button
                        type="button"
                        onClick={() => setNoteEditing(true)}
                        className="mt-4 inline-flex min-h-10 items-center gap-2 rounded-full border border-[#CDB899] bg-white px-4 text-[12px] text-[#4A3728]"
                        data-testid="brand-reference-v2-note-edit"
                      >
                        <Pencil size={13} /> Редактировать
                      </button>
                    </div>
                  )}
                </div>

                {!confirmDelete ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setConfirmDelete(true)}
                    className="mt-7 inline-flex min-h-11 items-center gap-2 rounded-full border border-[#DEC8BF] bg-white px-4 text-[12.5px] text-[#7A4E43] disabled:opacity-45"
                    data-testid="brand-reference-v2-delete"
                  >
                    <Trash2 size={14} /> Удалить референс
                  </button>
                ) : (
                  <div
                    className="mt-7 rounded-[14px] border border-[#E2CFC8] bg-[#FFF8F4] p-4"
                    data-testid="brand-reference-v2-delete-confirm"
                  >
                    <p className="text-[12.5px] leading-5 text-[#6B443A]">
                      Удалить этот референс из подборки?
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={remove}
                        className="inline-flex min-h-10 items-center gap-2 rounded-full bg-[#6B443A] px-4 text-[12px] text-white disabled:opacity-45"
                        data-testid="brand-reference-v2-delete-confirm-action"
                      >
                        {busy && <Loader2 size={14} className="animate-spin" />}
                        Удалить
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => setConfirmDelete(false)}
                        className="min-h-10 rounded-full border border-[#D8C7AE] bg-white px-4 text-[12px] text-[#4A3728]"
                      >
                        Оставить
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

